/**
 * SMS Marketing API client.
 *
 * X-Tenant-Id injection
 * ---------------------
 * When a super-admin selects a specific org, every outbound request must carry
 * the `X-Tenant-Id` header so the backend's tenant middleware overrides the
 * default JWT/hostname-resolved org.
 *
 * client.ts must not import useAdminStore directly (that creates a circular
 * dependency: admin.ts -> client.ts -> admin.ts). The cycle is broken by a
 * late-binding provider pattern:
 *
 *   1. client.ts holds a module-level `_getSelectedOrgId` slot (initially null).
 *   2. lib/admin.ts calls `registerOrgIdProvider()` right after the store is
 *      created, passing a getter closure.
 *   3. The Axios interceptor calls the getter at request time -- after the store
 *      has been initialised -- so there is no timing issue.
 *
 * All SMS endpoints live under `/api/sms/*` (the L1 sms-api contract), so query
 * and mutation paths in this app are absolute `/api/sms/...` strings.
 *
 * @module api/client
 */

import { createFetcher } from "@plerity/data-access";
import type { InternalAxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../config";

/** Slot for the selectedOrgId getter -- populated by lib/admin.ts at startup. */
let _getSelectedOrgId: (() => number | null) | null = null;

/**
 * Register the org-id provider.
 *
 * Called once by lib/admin.ts after the Zustand store is created so that
 * client.ts does not need to import the admin module directly.
 */
export function registerOrgIdProvider(fn: () => number | null): void {
  _getSelectedOrgId = fn;
}

/**
 * Inject X-Tenant-Id header when a super-admin has selected a specific org.
 * The backend's tenant middleware checks this header before falling back to
 * JWT/hostname resolution, so this effectively switches the active org for every
 * API call made during an admin session.
 */
function onRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const selectedOrgId = _getSelectedOrgId?.() ?? null;
  if (selectedOrgId !== null) {
    config.headers.set("X-Tenant-Id", String(selectedOrgId));
  }
  return config;
}

/** SMS Marketing API client -- uses shared createFetcher for consistent defaults. */
export const client = createFetcher({
  baseURL: API_BASE_URL,
  withCredentials: true,
  onRequest,
});
